import { forwardRef, KeyboardEvent, PointerEvent, useEffect, useRef, useState } from 'react';

type SliderRef = {};

export const Slider = forwardRef<
  SliderRef,
  {
    value: number | null;
    onChange: (value: number | null) => void;
    min: number;
    max: number;
    roundTo: number;
    suffix: string;
    accessibilityLabel?: string;
    disabled?: boolean;
  }
>(({ value, min, max, roundTo, onChange, suffix, accessibilityLabel, disabled }, _ref) => {
  const [dragValue, setDragValue] = useState<number | null>(null);
  const pointerActive = useRef(false);
  const effectiveValue = Math.min(max, Math.max(min, value ?? max));
  const displayedValue = Math.min(max, Math.max(min, dragValue ?? effectiveValue));
  const roundedValue = Math.min(max, Math.max(min, Math.round(displayedValue / roundTo) * roundTo));
  const progress = max > min ? ((displayedValue - min) / (max - min)) * 100 : 0;

  useEffect(() => {
    setDragValue(null);
    if (disabled) pointerActive.current = false;
  }, [value, min, max, disabled]);

  function commitValue(nextValue: number) {
    if (disabled) return;

    const roundedValue = Math.round(nextValue / roundTo) * roundTo;
    onChange(Math.min(max, Math.max(min, roundedValue)));
  }

  function finishPointer(event: PointerEvent<HTMLInputElement>) {
    if (!pointerActive.current) return;

    pointerActive.current = false;
    commitValue(event.currentTarget.valueAsNumber);
    setDragValue(null);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    let nextValue: number | undefined;

    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      nextValue = effectiveValue - roundTo;
    }
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      nextValue = effectiveValue + roundTo;
    }
    if (event.key === 'PageDown') nextValue = effectiveValue - roundTo * 10;
    if (event.key === 'PageUp') nextValue = effectiveValue + roundTo * 10;
    if (event.key === 'Home') nextValue = min;
    if (event.key === 'End') nextValue = max;

    if (nextValue === undefined) return;

    event.preventDefault();
    commitValue(nextValue);
  }

  return (
    <div
      className={`relative rounded-full py-3 has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-4 ${disabled ? 'opacity-30' : ''}`}
    >
      <div className="h-1.5 rounded-full bg-stone-200" aria-hidden="true">
        <div
          className="bg-brand-brown-dark h-full rounded-l-full"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div
        className={`pointer-events-none absolute top-0.5 -ml-3 size-6 rounded-full border-2 bg-white ${dragValue === null ? 'border-brand-brown-light' : 'scale-[1.2] border-black'}`}
        style={{ left: `${progress}%` }}
        aria-hidden="true"
      />

      <span
        className={`bg-brand-brown-dark pointer-events-none absolute -top-7 -ml-10 w-20 rounded-full py-1 text-center text-xs font-bold text-white ${dragValue === null ? 'opacity-0' : ''}`}
        style={{ left: `${progress}%`, lineHeight: 'normal' }}
        aria-hidden="true"
      >
        {roundedValue} {suffix}
      </span>

      {/* Extend the native range by its thumb width so its endpoints match the visible track. */}
      <div className="absolute -inset-x-3 inset-y-0">
        <input
          type="range"
          min={min}
          max={max}
          step="any"
          value={displayedValue}
          disabled={disabled}
          aria-label={accessibilityLabel}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={roundedValue}
          aria-valuetext={`${roundedValue} ${suffix}`}
          aria-disabled={Boolean(disabled)}
          className="m-0 block size-full touch-none appearance-none bg-transparent p-0 opacity-0 [&::-moz-range-thumb]:size-6 [&::-moz-range-thumb]:border-0 [&::-webkit-slider-thumb]:size-6 [&::-webkit-slider-thumb]:appearance-none"
          onPointerDown={(event) => {
            if (disabled || event.button !== 0) return;

            pointerActive.current = true;
            setDragValue(effectiveValue);
          }}
          onChange={(event) => {
            if (pointerActive.current) {
              setDragValue(event.currentTarget.valueAsNumber);
            } else {
              commitValue(event.currentTarget.valueAsNumber);
            }
          }}
          onPointerUp={finishPointer}
          onPointerCancel={finishPointer}
          onLostPointerCapture={finishPointer}
          onKeyDown={handleKeyDown}
        />
      </div>
    </div>
  );
});

Slider.displayName = 'Slider';
